import { createContext, useContext, forwardRef } from "react";
import {
  Link as RRLink,
  NavLink as RRNavLink,
} from "react-router-dom";

/**
 * Base-path shim so the standard Service Template's hard-coded absolute
 * paths (`<Link to="/consultations" />` etc.) navigate to
 * `<basePath>/consultations` when the template is mounted at
 * `/site/<slug>` on a published site.
 *
 * The template files import Link/NavLink from THIS module instead of
 * `react-router-dom` directly. All other react-router-dom exports still
 * come from the real package.
 *
 * When basePath is `""` (default) the shim is a pass-through, so the
 * template continues to work standalone (e.g. under `/template/service/*`
 * previews or in isolation).
 */
const ServiceBasePathContext = createContext("");

export function ServiceBasePathProvider({ basePath = "", children }) {
  return (
    <ServiceBasePathContext.Provider value={basePath || ""}>
      {children}
    </ServiceBasePathContext.Provider>
  );
}

export function useServiceBasePath() {
  return useContext(ServiceBasePathContext);
}

/** Prefix an absolute `to` path with the current basePath. Non-string /
 * external / hash-only targets pass through untouched. */
function withBase(to, basePath) {
  if (!basePath) return to;
  if (typeof to !== "string") return to;
  if (!to.startsWith("/")) return to; // relative / hash / empty → leave alone
  if (to.startsWith("//")) return to; // protocol-relative
  // Prevent double-prefixing if the caller already produced a full path.
  if (to === basePath || to.startsWith(basePath + "/")) return to;
  return basePath + to;
}

export const Link = forwardRef(function Link({ to, ...rest }, ref) {
  const basePath = useServiceBasePath();
  return <RRLink ref={ref} to={withBase(to, basePath)} {...rest} />;
});

export const NavLink = forwardRef(function NavLink({ to, ...rest }, ref) {
  const basePath = useServiceBasePath();
  return <RRNavLink ref={ref} to={withBase(to, basePath)} {...rest} />;
});
